import {useState, useEffect, useMemo} from 'react';
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   PointElement,
   Title,
   Tooltip as ChartTooltip,
   Legend,
   ArcElement
} from 'chart.js';
import {Box, Container, Grid, Slider, Typography} from '@mui/material';
import {Pie} from 'react-chartjs-2';
import {dataPieChar} from '../../utils/types/pieChart.types';

// Registrar los componentes necesarios
ChartJS.register(CategoryScale, LinearScale, PointElement, Title, ChartTooltip, Legend, ArcElement);

interface PieDateChartProps {
   title: string;
   data: dataPieChar[];
   defaultSlider?: {start: number; end: number};
   categories: {[key: string]: string};
}

interface Mark {
   value: number;
   label: string;
}

export const PieChart: React.FC<PieDateChartProps> = ({
   title,
   data,
   categories,
   defaultSlider = {end: 4, start: 0}
}) => {
   // Estado del Slider
   const [valueSlider, setValueSlider] = useState<[number, number]>([
      defaultSlider.start,
      defaultSlider.end
   ]);
   const [marks, setMarks] = useState<Mark[]>([]);
   const [monthLabels, setMonthLabels] = useState<string[]>([]);

   // Estado para los datos
   const [dataViewChart, setDataViewChart] = useState<dataPieChar[]>([]);

   const handleSliderChange = (event: Event, newValue: number | number[]) => {
      const [mesInicial, mesFinal] = newValue as [number, number];
      setValueSlider([mesInicial, mesFinal]);
   };

   const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
         tooltip: {
            backgroundColor: '#fff',
            titleColor: '#333',
            bodyColor: '#333',
            borderColor: '#ccc',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: true,
            padding: 8,
            boxPadding: 6,
            boxWidth: 10,
            boxHeight: 10
         }
      }
   };

   // Calculamos los datos del gráfico
   const chartData = useMemo(() => {
      const categorizedData = dataViewChart.reduce((acc, item) => {
         item.subData.forEach(({etiqueta, value}) => {
            if (!acc[etiqueta]) {
               acc[etiqueta] = 0;
            }
            acc[etiqueta] += value;
         });
         return acc;
      }, {} as {[key: string]: number});
      const dataForChart = Object.keys(categories).map(category => categorizedData[category] || 0);
      return {
         labels: Object.values(categories),
         datasets: [
            {
               label: '# de llaves',
               data: dataForChart,
               backgroundColor: [
                  'rgba(255, 99, 132, 0.2)',
                  'rgba(54, 162, 235, 0.2)',
                  'rgba(75, 192, 192, 0.2)',
                  'rgba(255, 159, 64, 0.2)',
                  'rgba(153, 102, 255, 0.2)'
               ],
               borderColor: [
                  'rgba(255, 99, 132, 1)',
                  'rgba(54, 162, 235, 1)',
                  'rgba(75, 192, 192, 1)',
                  'rgba(255, 159, 64, 1)',
                  'rgba(153, 102, 255, 1)'
               ],
               borderWidth: 1
            }
         ]
      };
   }, [dataViewChart]);

   // Actualizamos los datos según el slider
   useEffect(() => {
      if (data && data.length > 0) {
         const [start, end] = valueSlider;
         setDataViewChart(data.slice(start, end + 1));
      }
   }, [valueSlider, data]);

   useEffect(() => {
      if (data) {
         const newMarks = data.map((currRow, index) => ({
            value: index,
            label: currRow.mark
         }));
         const newLabels: string[] = data.map(currRow => currRow.denominacion);
         setMarks(newMarks);
         setMonthLabels(newLabels);
      }
   }, [data]);

   return (
      <Container sx={{height: '100%', maxHeight: '80vh'}}>
         <Grid item xs={12} alignContent="center" justifyContent="center" sx={{height: '43px'}}>
            <Typography variant="h5" mt={0.5} textAlign="center" lineHeight={1.1}>
               {title ?? ''}
            </Typography>
         </Grid>
         <Grid item xs={12} sx={{height: 'calc(100% - 100px)'}}>
            <Pie data={chartData} options={options} />
         </Grid>
         <Grid item xs={12}>
            <Box style={{width: '90%', margin: 'auto'}}>
               <Slider
                  value={valueSlider}
                  onChange={handleSliderChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={marks.length - 1}
                  marks={marks}
                  step={1}
                  valueLabelFormat={index => monthLabels[index]}
                  style={{fontSize: '5px important!'}}
               />
            </Box>
         </Grid>
      </Container>
   );
};
